import type { Tag, Group, GroupMembership, UserRole } from '@/types/models'

interface ItemWithId {
  id: string
}

/**
 * Filters items (tasks or care reasons) based on group access control.
 *
 * Rules:
 * - Admins see everything
 * - Items not in any group are public (visible to all)
 * - Items in public groups are visible to all
 * - Items in private groups require user membership
 */
export function filterAccessibleItems<T extends ItemWithId>(
  items: T[],
  userId: string,
  userRole: UserRole,
  tags: Tag[],
  groups: Group[],
  memberships: GroupMembership[],
  tagField: 'taskId' | 'reasonId',
): T[] {
  // Admins and superadmins see everything
  if (userRole === 'admin' || userRole === 'superadmin') return items

  // Volunteers only see items assigned directly to them
  if (userRole === 'volunteer') {
    return items.filter(
      (item) =>
        'assignedUserId' in item &&
        (item as Record<string, unknown>).assignedUserId === userId,
    )
  }

  // Build lookup maps
  const groupMap = new Map(groups.map((g) => [g.id, g]))
  const userGroupIds = new Set(
    memberships.filter((m) => m.userId === userId).map((m) => m.groupId),
  )

  // Build item → groupIds mapping
  const itemGroupIds = new Map<string, Set<string>>()
  for (const tag of tags) {
    const itemId = tag[tagField]
    if (!itemId) continue
    if (!itemGroupIds.has(itemId)) {
      itemGroupIds.set(itemId, new Set())
    }
    itemGroupIds.get(itemId)!.add(tag.groupId)
  }

  return items.filter((item) => {
    const groupIds = itemGroupIds.get(item.id)

    // Items not in any group are public
    if (!groupIds || groupIds.size === 0) return true

    // Check each group the item belongs to
    for (const groupId of groupIds) {
      const group = groupMap.get(groupId)
      if (group?.isPrivate && !userGroupIds.has(groupId)) {
        return false
      }
    }

    return true
  })
}
